/**
 * DashboardAcessos.js — indicadores das verticais sem ticket (Onda 55)
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * No Clube Social e no Condomínio não existe ticket: a leitura é o próprio
 * fato, e a saída pode nem ser vista (cancela por QR Code no clube, botoeira
 * interna no condomínio). O painel de estacionamento, que mede receita, ticket
 * médio e permanência, mostrava zero em tudo — no Ipê, com dezenas de
 * reconhecimentos no banco, todos os cartões estavam zerados.
 *
 * Aqui a unidade é o acesso. Nada nesta tela consulta ticket. O visual
 * reaproveita as classes do painel de estacionamento, para as duas telas serem
 * reconhecivelmente a mesma plataforma.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { MESES, mesCorrente, deslocarMes, rotuloMes, inteiro, Kpi } from './Dashboard';
import '../styles/Dashboard.css';

/** Como cada vertical chama o que o motor grava em `access_type`. */
const ROTULOS = {
  associado_clube:   'Associados',
  associado_inativo: 'Associados inativos',
  morador:           'Moradores',
  visitante:         'Visitantes',
  negado:            'Negados',
  avulso:            'Não cadastrados',
  mensalista:        'Mensalistas',
  credenciado:       'Credenciados',
  nao_classificado:  'Sem classificação',
};

const DashboardAcessos = () => {
  const { vertical } = useAuth();
  const ehClube = vertical === 'clube_social';
  const [mes, setMes] = useState(mesCorrente);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const buscar = useCallback(async (mesAlvo) => {
    setCarregando(true);
    try {
      const r = await api.get('/api/dashboard/access-overview', { params: { month: mesAlvo } });
      setDados(r.data);
      setErro(null);
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível carregar os indicadores de acesso.');
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { buscar(mes); }, [mes, buscar]);

  // Só o mês corrente se atualiza sozinho: um mês fechado não muda mais.
  useEffect(() => {
    if (mes !== mesCorrente()) return undefined;
    const t = setInterval(() => buscar(mes), 60000);
    return () => clearInterval(t);
  }, [mes, buscar]);

  const anos = useMemo(() => {
    const atual = new Date().getFullYear();
    return [atual, atual - 1, atual - 2];
  }, []);

  const [anoSel, mesSel] = mes.split('-').map(Number);
  const ehMesCorrente = mes === mesCorrente();

  const graficoDiario = useMemo(() => ({
    labels: (dados?.diario || []).map(d => String(d.dia).slice(8, 10)),
    datasets: [
      { label: 'Entradas', data: (dados?.diario || []).map(d => d.entradas), backgroundColor: '#14657a', borderRadius: 3 },
      { label: 'Saídas',   data: (dados?.diario || []).map(d => d.saidas),   backgroundColor: '#8fbecb', borderRadius: 3 },
    ],
  }), [dados]);

  // As 24 horas aparecem sempre, para o vazio da madrugada ser visível em vez
  // de sumir do eixo — é o que mostra a diferença entre "não houve movimento"
  // e "a câmera não estava lendo".
  const graficoHorario = useMemo(() => {
    const porHora = new Map((dados?.por_hora || []).map(h => [h.hora, h]));
    const horas = Array.from({ length: 24 }, (_, h) => porHora.get(h) || { hora: h, entradas: 0, saidas: 0 });
    return {
      labels: horas.map(h => `${String(h.hora).padStart(2, '0')}h`),
      datasets: [
        { label: 'Entradas', data: horas.map(h => h.entradas), backgroundColor: '#14657a', borderRadius: 3 },
        { label: 'Saídas',   data: horas.map(h => h.saidas),   backgroundColor: '#8fbecb', borderRadius: 3 },
      ],
    };
  }, [dados]);

  const opcoes = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 }, color: '#5b7280' } },
      tooltip: { backgroundColor: '#10202a', padding: 10, cornerRadius: 6, titleFont: { size: 12 }, bodyFont: { size: 12 } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#7d939f', font: { size: 11 } } },
      y: { beginAtZero: true, grid: { color: '#ebf0f3' }, ticks: { color: '#7d939f', font: { size: 11 }, precision: 0 } },
    },
  };

  const k = dados?.kpis;

  const picoHora = useMemo(() => {
    if (!dados?.por_hora?.length) return null;
    return dados.por_hora.reduce((a, b) => (b.acessos > a.acessos ? b : a));
  }, [dados]);

  const totalComposicao = useMemo(
    () => (dados?.composicao || []).reduce((s, c) => s + c.acessos, 0),
    [dados]
  );

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1>Acessos</h1>
          <p className="page-subtitle">
            {ehClube
              ? 'Reconhecimentos na portaria do clube. Ticket, cobrança e cancela ficam no sistema de gestão.'
              : 'Reconhecimentos na portaria. Cada leitura é um acesso; a saída aparece quando há câmera nela.'}
            {' '}Comparado com {rotuloMes(dados?.mes_anterior || deslocarMes(mes, -1))}.
          </p>
        </div>

        <div className="periodo">
          <button className="btn btn-secondary btn-sm" onClick={() => setMes(deslocarMes(mes, -1))} title="Mês anterior">
            <FiChevronLeft />
          </button>
          <select value={mesSel} onChange={(e) => setMes(`${anoSel}-${String(Number(e.target.value)).padStart(2, '0')}`)} aria-label="Mês">
            {MESES.map((nome, i) => <option key={nome} value={i + 1}>{nome}</option>)}
          </select>
          <select value={anoSel} onChange={(e) => setMes(`${e.target.value}-${String(mesSel).padStart(2, '0')}`)} aria-label="Ano">
            {anos.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setMes(deslocarMes(mes, 1))}
            disabled={ehMesCorrente}
            title={ehMesCorrente ? 'Este já é o mês corrente' : 'Mês seguinte'}
          >
            <FiChevronRight />
          </button>
          {!ehMesCorrente && (
            <button className="btn btn-secondary btn-sm" onClick={() => setMes(mesCorrente())}>Mês atual</button>
          )}
        </div>
      </div>

      {erro && <div className="painel-erro">{erro}</div>}

      {carregando && !dados ? (
        <div className="loading"><div className="spinner" /><p>Carregando indicadores...</p></div>
      ) : dados && k && (
        <>
          <div className="kpis">
            <Kpi
              rotulo="Acessos no mês"
              valor={inteiro(k.acessos.value)}
              indicador={k.acessos}
              formatar={inteiro}
              polaridade="neutra"
              nota={`média de ${inteiro(k.media_diaria.value)} por dia com movimento`}
            />
            <Kpi
              rotulo="Veículos distintos"
              valor={inteiro(k.veiculos.value)}
              indicador={k.veiculos}
              formatar={inteiro}
              polaridade="neutra"
              nota={k.acessos_por_veiculo.value !== null
                ? `${k.acessos_por_veiculo.value} acessos por veículo no mês`
                : null}
            />
            <Kpi rotulo="Entradas" valor={inteiro(k.entradas.value)} indicador={k.entradas} formatar={inteiro} polaridade="neutra" />
            <Kpi
              rotulo="Saídas"
              valor={inteiro(k.saidas.value)}
              indicador={k.saidas}
              formatar={inteiro}
              polaridade="neutra"
              nota={k.saidas.value === 0 ? 'nenhuma câmera registrando saída' : null}
            />
            <Kpi rotulo="Acessos negados" valor={inteiro(k.negados.value)} indicador={k.negados} formatar={inteiro} polaridade="negativa" />
          </div>

          <div className="graficos">
            <section className="grafico">
              <header>
                <h2>Movimento por dia</h2>
                <span>{rotuloMes(dados.mes)}</span>
              </header>
              {dados.diario.length === 0
                ? <p className="no-data">Nenhum acesso registrado neste mês</p>
                : <div className="grafico-area"><Bar data={graficoDiario} options={opcoes} /></div>}
            </section>

            <section className="grafico">
              <header>
                <h2>Acessos por hora do dia</h2>
                {picoHora && picoHora.acessos > 0 && (
                  <span>pico às {String(picoHora.hora).padStart(2, '0')}h, {inteiro(picoHora.acessos)} acessos</span>
                )}
              </header>
              {dados.por_hora.length === 0
                ? <p className="no-data">Sem dados de horário neste mês</p>
                : <div className="grafico-area"><Bar data={graficoHorario} options={opcoes} /></div>}
            </section>
          </div>

          <div className="graficos">
            <section className="top-placas">
              <header><h2>Quem acessou</h2></header>
              {dados.composicao.length === 0 ? (
                <p className="no-data">Nenhum acesso classificado neste mês</p>
              ) : (
                <ol>
                  {dados.composicao.map((c, i) => {
                    const pct = totalComposicao ? Math.round((c.acessos / totalComposicao) * 1000) / 10 : 0;
                    const maior = dados.composicao[0].acessos || 1;
                    return (
                      <li key={c.tipo}>
                        <span className="pos">{i + 1}</span>
                        <span className="placa">{ROTULOS[c.tipo] || c.tipo}</span>
                        <span className="barra" aria-hidden="true">
                          <span style={{ width: `${(c.acessos / maior) * 100}%` }} />
                        </span>
                        <span className="visitas">{inteiro(c.acessos)} · {pct}%</span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            <section className="top-placas">
              <header><h2>Veículos mais frequentes no mês</h2></header>
              {dados.mais_frequentes.length === 0 ? (
                <p className="no-data">Nenhum acesso registrado neste mês</p>
              ) : (
                <ol>
                  {dados.mais_frequentes.map((v, i) => (
                    <li key={v.plate}>
                      <span className="pos">{i + 1}</span>
                      <span className="placa">{v.plate}</span>
                      <span className="barra" aria-hidden="true">
                        <span style={{ width: `${(v.acessos / dados.mais_frequentes[0].acessos) * 100}%` }} />
                      </span>
                      <span className="visitas">{inteiro(v.acessos)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>

          <div className="graficos">
            <section className="top-placas">
              <header><h2>Por câmera</h2></header>
              {dados.por_camera.length === 0 ? (
                <p className="no-data">Nenhuma câmera com leitura no período</p>
              ) : (
                <ol>
                  {dados.por_camera.map((c, i) => (
                    <li key={`${c.camera_id}-${c.log_type}`}>
                      <span className="pos">{i + 1}</span>
                      <span className="placa">{c.camera_name || `Câmera ${c.camera_id}`} · {c.log_type}</span>
                      <span className="barra" aria-hidden="true">
                        <span style={{ width: `${(c.acessos / dados.por_camera[0].acessos) * 100}%` }} />
                      </span>
                      <span className="visitas">{inteiro(c.acessos)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <section className="top-placas">
              <header>
                <h2>Qualidade da leitura</h2>
                <span>sem cobrança envolvida, é o indicador de saúde da operação</span>
              </header>
              <ol>
                <li>
                  <span className="placa">Confiança média</span>
                  <span className="visitas">{dados.qualidade.confianca_media !== null ? dados.qualidade.confianca_media.toFixed(3) : '—'}</span>
                </li>
                <li>
                  <span className="placa">Largura média da placa</span>
                  <span className="visitas">{dados.qualidade.largura_media !== null ? `${inteiro(dados.qualidade.largura_media)} px` : '—'}</span>
                </li>
                <li>
                  <span className="placa">Revisadas pelo operador</span>
                  <span className="visitas">{inteiro(dados.qualidade.revisadas)}</span>
                </li>
                <li>
                  <span className="placa">Corrigidas na revisão</span>
                  <span className="visitas">{inteiro(dados.qualidade.corrigidas)}</span>
                </li>
              </ol>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardAcessos;
